/**
 * React state hook owning the live `HighLiftAirfoil` design + operating point for the native
 * (no-iframe) high-lift foil tool. This is the React replacement for the module-level `cfg` and
 * the slider getters/setters in the vendored webui `preview.ts`: the config is held in React
 * state, edits go through typed setters, and the geometry / gap-metrics / sweep-input / flow
 * derivations are memoised off the config.
 *
 * The flat-axis surface (the schema Tailor speaks — see `applyAxes`) mirrors the webui
 * `thread.ts` `applyAxesToConfig` flat→nested mapping verbatim, so a Tailor suggestion's
 * `values` record drives this tool exactly as it drove the iframe.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_HIGH_LIFT_AIRFOIL,
  FOLLOWER_DEPLOY_TRACK,
  FOLLOWER_MOTION,
  PURE_ROTATION_MOTION,
  buildConfiguration,
  gapMetrics,
  gapMetricOutputs,
  buildSweepInputs,
  flowConditions,
  migrateConfig,
  elementsOf,
  blowingOf,
  makeVaneElement,
  makeBlowingSource,
  addPlainFlap as addPlainFlapToDesign,
  addSlat as addSlatToDesign,
  addKrueger as addKruegerToDesign,
  addSingleSlottedFlap as addSingleSlottedFlapToDesign,
  addSlottedFlapSystem as addSlottedFlapSystemToDesign,
  isDefaultCollectionId,
  coveFromHinge,
  designFingerprint,
  flattenDesign,
  type HighLiftAirfoil,
  type Configuration,
  type Element,
  type BlowingSource,
  type GapMetrics,
  type SlotMetrics,
  type FlowConditions,
  type SweepInputs,
  type V2,
  type AirfoilCoords,
  type VaneFollower,
} from '../geometry';
import { KNOB_REGISTRY, clampToKnob, knobBounds, type KnobCtx } from './knobRegistry';
import { motionGapCheck, type MotionGapCheck } from '../geometry/motionGapCheck';
import { useUndoRedoStore } from '../../thread-v5/undoRedoStore';
import { type ParamSpec, unpackFullVector } from '../design-opt/paramSpec';
import { solveInverse, optimizeScalar } from '../design-opt/inverseDesign';
import { cpAtFactory, surfaceCpFromNN, type ForwardFn } from '../design-opt/nnDesign';
import { peakSuction, type SurfaceCp } from '../design-opt/metrics';
import type { LMOptions } from '../design-opt/lm';
import type { NelderMeadOptions } from '../design-opt/inverseDesign';
import type { NN } from '../flowfield-nn/nnInfer';
import { nnSurfaceForces, type NNCond, type FoilElement } from '../flowfield-nn/nnAero';
import type { SurfaceCpElement } from './FlowVisLayer';

/** Debounce a value: returns the latest `value` only after it has been stable for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export type FlaperonElementField = 'upperCutX' | 'lowerCutX' | 'noseTipX' | 'noseTipZ';
export type BlowingSourceField = 'x' | 'z' | 'diameter' | 'tiltDeg';
/**
 * Editable NUMERIC scalar fields on a Krueger element (Rig table / shape editor). The originals
 * (lower hinge x, forward nose cut) plus the FXC-9009 rebuild's riggable leaf scalars (panel arc /
 * camber, real bullnose radius, panel thickness, and the slotted-throat gap / overlap). The `variant`
 * enum is NOT here — it's a two-state string toggle, set via {@link setKruegerVariant}, not this union.
 */
export type KruegerElementField =
  | 'lowerHingeX'
  | 'noseCutX'
  | 'panelArcFraction'
  | 'panelCamber'
  | 'bullnoseRadius'
  | 'panelThickness'
  | 'gap'
  | 'overlap';

/** Undo/redo scope key for the high-lift foil design surface. */
export const FOIL_UNDO_SCOPE = 'foil-design';

const KNOB_BY_KEY = new Map(KNOB_REGISTRY.map((def) => [def.key, def]));

// ---------------------------------------------------------------------------
// Baseline↔buffer model (FXC-9009, XFOIL buffer-foil → Thread lineage)
// ---------------------------------------------------------------------------
//
// XFOIL has a CURRENT airfoil (analysed) and a BUFFER (the scratch copy reshaped in GDES). This hook
// mirrors that: the live `config` is the BUFFER (freely reshaped — drag handles, sliders, undo/redo);
// a saved/loaded/committed geometry is the BASELINE ("current"). `isDirty` is true when the buffer's
// design diverges from the baseline. The seed is the default baseline. Loading or committing a
// geometry RE-BASELINES (the buffer becomes clean against the newly loaded/committed shape).

/** A reference to the baseline ("current") geometry the buffer is diffed against. */
export interface BaselineRef {
  /** The committed/loaded/seed source: how the baseline came to be. */
  source: 'seed' | 'loaded' | 'committed';
  /** Human name of the baseline geometry (the saved/committed name; the seed default has none). */
  name: string | null;
  /** Saved-geometry slug, when the baseline is a saved/committed node (drives the Lineage deep-link). */
  slug: string | null;
  /** Saved-geometry node id, when known (the Lineage pivot the variant derives from). */
  nodeId: string | null;
  /** The baseline's design fingerprint (matches {@link designFingerprint} of its config). */
  fingerprint: string;
}

/** The seed baseline — the default reference geometry, never a saved node. */
function seedBaseline(config: HighLiftAirfoil): BaselineRef {
  return { source: 'seed', name: null, slug: null, nodeId: null, fingerprint: designFingerprint(config) };
}

/**
 * The draggable control-point handles on the foil canvas. Each maps to one config path
 * via {@link FoilSetters} (see DRAG_FACTS): the vane TE + its 4 B-spline points, the
 * flaperon nose tip (P2), the deploy + flaperon hinge pivots, and the actuator-disk centre.
 * Handles whose config has no clean setter (flaperon cut points / tangent handles, deploy-track
 * waypoints) are intentionally absent — they are non-draggable in v1.
 */
export type FoilHandleId =
  | 'vane-te'
  | 'vane-0'
  | 'vane-1'
  | 'vane-2'
  | 'vane-3'
  | 'flap-nosetip'
  | 'deploy-hinge'
  | 'flaperon-hinge'
  | 'prop-center';

/** All draggable handle ids, in a stable draw/hit-test order. */
export const FOIL_HANDLE_IDS: readonly FoilHandleId[] = [
  'vane-te',
  'vane-0',
  'vane-1',
  'vane-2',
  'vane-3',
  'flap-nosetip',
  'deploy-hinge',
  'flaperon-hinge',
  'prop-center',
] as const;

/** A draggable handle with its current STOWED config position + drawing metadata. */
export interface FoilHandle {
  /** Stable handle id (drives {@link setControlPoint}). */
  id: FoilHandleId;
  /** Current position [x, z] in STOWED config coords (the value stored in the config). */
  stowed: V2;
  /** Short label drawn next to the handle. */
  label: string;
  /** Which element the handle belongs to (for the hover read-out + colour). */
  element: 'vane' | 'flaperon' | 'deploy' | 'propeller';
}

/**
 * List each draggable handle's CURRENT stowed [x, z] from a config, with its label + element.
 * The canvas carries these through the deploy transform to get screen positions (and inverts
 * that transform to map a drag back to a config edit). Stowed coords are what the config stores,
 * so this is a direct read of the config paths in {@link FoilHandleId} order.
 */
export function foilHandles(cfg: HighLiftAirfoil): FoilHandle[] {
  const d = cfg.design;
  const handles: FoilHandle[] = [
    { id: 'vane-te', stowed: [d.vane.te[0], d.vane.te[1]], label: 'vane TE', element: 'vane' },
  ];
  d.vane.points.forEach((p, i) => {
    handles.push({
      id: `vane-${i}` as FoilHandleId,
      stowed: [p[0], p[1]],
      label: `vane P${i + 1}`,
      element: 'vane',
    });
  });
  handles.push(
    {
      id: 'flap-nosetip',
      stowed: [d.flaperon.noseTip[0], d.flaperon.noseTip[1]],
      label: 'flap nose',
      element: 'flaperon',
    },
    {
      id: 'deploy-hinge',
      stowed: [d.deployHinge.pivot[0], d.deployHinge.pivot[1]],
      label: 'deploy hinge',
      element: 'deploy',
    },
    {
      id: 'flaperon-hinge',
      stowed: [d.flaperonHinge.pivot[0], d.flaperonHinge.pivot[1]],
      label: 'flap hinge',
      element: 'flaperon',
    },
    {
      id: 'prop-center',
      stowed: [cfg.propeller.x, cfg.propeller.z],
      label: 'disk',
      element: 'propeller',
    },
  );
  return handles;
}

// ---------------------------------------------------------------------------
// AC-H — DATA-DRIVEN handle descriptors (per-element, not a fixed enum)
// ---------------------------------------------------------------------------
//
// The fixed {@link FoilHandleId} enum + {@link foilHandles} above are the v1 model — a hard-coded
// set of handles for the DEFAULT section only. AC-H replaces it with descriptors GENERATED from the
// element collection: each descriptor carries its owning element id + collection index, so an ADDED
// vane's TE/P1..P4 handles appear automatically (and write back to that vane's own shape, not the
// legacy default fields). The descriptors are the new source of truth; the enum-style ids above are
// kept only as stable strings inside the default descriptors so the existing tests / labels match.

/** Which deploy-frame transform a handle rides, so the canvas can map a drag back to stowed coords. */
export type HandleXformKind =
  /** Rides the vane/assembly deploy transform (`move`). */
  | 'deploy'
  /** Vane/flaperon-derived deploy transform AND the flaperon's own hinge rotation. */
  | 'flap-nose'
  /** Drawn at raw config coords (deploy hinge pivot, actuator disk). */
  | 'identity';

/**
 * A single draggable control point, derived from the element collection. Unlike {@link FoilHandle}
 * (the fixed-enum shape), a descriptor owns its config write directly via {@link set}, so a new
 * element kind adds handles without touching a central switch. `id` is stable + unique
 * (`<elementId>:<role>`), so it survives a config edit (the canvas keys hover/drag off it).
 */
export interface FoilHandleDescriptor {
  /** Stable, unique handle id (`<elementId>:<role>`). */
  id: string;
  /** Current position in STOWED config coords (the value the config stores). */
  stowed: V2;
  /** Short label drawn next to the handle + shown in the "what am I dragging" read-out. */
  label: string;
  /** The collection element this handle belongs to (drives selection + colour). */
  elementId: string;
  /** Handle accent family — maps to the palette via the canvas (`vane`/`flaperon`/`deploy`/`propeller`). */
  family: FoilHandle['element'];
  /** Which deploy-frame transform this handle rides (so the canvas inverts the right one on drag). */
  xform: HandleXformKind;
  /** Write the dragged STOWED position back onto a draft config (caller passes a fresh clone to mutate). */
  set: (draft: HighLiftAirfoil, worldX: number, worldZ: number) => void;
}

/** Clamp a value to [lo, hi] (cut points must stay in range). */
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Generate the data-driven handle descriptors for a config, walking the element + blowing
 * collections (FXC-9009 §B.5). Every vane element contributes TE + P1..P4; every flaperon-derived
 * element contributes its nose tip + upper/lower cut points; every blowing source contributes its
 * disk centre; the deploy + flaperon hinges contribute one each. Added elements (their own cloned
 * `shape` / own id) get their own handles automatically — the `set` closure writes to that element's
 * shape object on the collection (which, for the default-role entries, is the SAME object as the
 * legacy `design.vane` / `design.flaperon` the sliders edit; see `deriveElements`/`rebindLegacyElements`).
 */
export function foilHandleDescriptors(cfg: HighLiftAirfoil): FoilHandleDescriptor[] {
  const out: FoilHandleDescriptor[] = [];
  const els = elementsOf(cfg);

  // Resolve the live element shape on a DRAFT clone by id (so `set` writes to the right object even
  // after structuredClone breaks the original references). Falls back to the legacy field for the
  // default-role ids, which keeps the default handles writing the same paths the sliders do.
  const draftVaneShape = (draft: HighLiftAirfoil, id: string) => {
    for (const el of elementsOf(draft)) if (el.id === id && el.kind === 'vane-bspline') return el.shape;
    return draft.design.vane;
  };
  const draftFlapShape = (draft: HighLiftAirfoil, id: string) => {
    for (const el of elementsOf(draft)) if (el.id === id && el.kind === 'flaperon-derived') return el.shape;
    return draft.design.flaperon;
  };

  for (const el of els) {
    if (el.kind === 'vane-bspline') {
      const sh = el.shape;
      out.push({
        id: `${el.id}:te`,
        stowed: [sh.te[0], sh.te[1]],
        label: `${el.label} · TE`,
        elementId: el.id,
        family: 'vane',
        xform: 'deploy',
        set: (draft, x, z) => {
          const s = draftVaneShape(draft, el.id);
          s.te[0] = x;
          s.te[1] = z;
        },
      });
      sh.points.forEach((p, i) => {
        out.push({
          id: `${el.id}:p${i}`,
          stowed: [p[0], p[1]],
          label: `${el.label} · P${i + 1}`,
          elementId: el.id,
          family: 'vane',
          xform: 'deploy',
          set: (draft, x, z) => {
            const s = draftVaneShape(draft, el.id);
            const pt = s.points[i];
            if (pt) {
              pt[0] = x;
              pt[1] = z;
            }
          },
        });
      });
    } else if (el.kind === 'flaperon-derived') {
      const sh = el.shape;
      out.push({
        id: `${el.id}:nose`,
        stowed: [sh.noseTip[0], sh.noseTip[1]],
        label: `${el.label} · nose tip`,
        elementId: el.id,
        family: 'flaperon',
        xform: 'flap-nose',
        set: (draft, x, z) => {
          const s = draftFlapShape(draft, el.id);
          s.noseTip[0] = x;
          s.noseTip[1] = z;
        },
      });
      out.push({
        id: `${el.id}:ucut`,
        // cut points are chordwise on the main surface; we draw + drag them at their stowed x with
        // the stored z, and only the x (the cut location) is written back.
        stowed: [sh.upperCutX, 0],
        label: `${el.label} · upper cut`,
        elementId: el.id,
        family: 'flaperon',
        xform: 'flap-nose',
        set: (draft, x) => {
          const s = draftFlapShape(draft, el.id);
          s.upperCutX = clamp(x, 0.5, 0.95);
        },
      });
      out.push({
        id: `${el.id}:lcut`,
        stowed: [sh.lowerCutX, 0],
        label: `${el.label} · lower cut`,
        elementId: el.id,
        family: 'flaperon',
        xform: 'flap-nose',
        set: (draft, x) => {
          const s = draftFlapShape(draft, el.id);
          s.lowerCutX = clamp(x, 0.5, 0.95);
        },
      });
    }
  }

  // Hinges (one assembly each) — drawn at raw config coords (identity transform).
  out.push({
    id: 'main:deploy-hinge',
    stowed: [cfg.design.deployHinge.pivot[0], cfg.design.deployHinge.pivot[1]],
    label: 'deploy hinge',
    elementId: 'main',
    family: 'deploy',
    xform: 'identity',
    set: (draft, x, z) => {
      draft.design.deployHinge.pivot[0] = x;
      draft.design.deployHinge.pivot[1] = z;
    },
  });
  out.push({
    id: 'main:flaperon-hinge',
    stowed: [cfg.design.flaperonHinge.pivot[0], cfg.design.flaperonHinge.pivot[1]],
    label: 'flaperon hinge',
    elementId: 'main',
    family: 'flaperon',
    xform: 'identity',
    set: (draft, x, z) => {
      draft.design.flaperonHinge.pivot[0] = x;
      draft.design.flaperonHinge.pivot[1] = z;
    },
  });

  // Actuator-disk centres (one per blowing source) — identity transform.
  blowingOf(cfg).forEach((src, idx) => {
    out.push({
      id: `${src.id}:center`,
      stowed: [src.x, src.z],
      label: `${src.label} · disk`,
      elementId: src.id,
      family: 'propeller',
      xform: 'identity',
      set: (draft, x, z) => {
        // First disk mirrors onto the legacy propeller (the sweep wire reads design.blowing[0]).
        const sources = blowingOf(draft);
        const s = sources.find((b) => b.id === src.id) ?? sources[idx];
        if (s) {
          s.x = x;
          s.z = z;
        }
        if (idx === 0) {
          draft.propeller.x = x;
          draft.propeller.z = z;
        }
      },
    });
  });

  return out;
}

/** A deep structural clone of the default config (every nested object/array is independent). */
function cloneConfig(cfg: HighLiftAirfoil): HighLiftAirfoil {
  return structuredClone(cfg);
}

/** Read a finite axis value from a flat record, or `undefined` when absent / non-finite. */
function axisValue(axes: Record<string, number>, key: string): number | undefined {
  if (!Object.prototype.hasOwnProperty.call(axes, key)) return undefined;
  const v = axes[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Mutate one slot of a `[number, number]` pair if both the pair and the value are present. */
function set2(pair: [number, number] | undefined, i: 0 | 1, value: number | undefined): void {
  if (pair && value !== undefined && Number.isFinite(value)) pair[i] = value;
}

/**
 * Write one disk-geometry field onto BOTH the legacy `propeller` and the canonical first blowing
 * source, keeping the dual representation consistent (FXC-9009 §B.5). The legacy `propeller` is a
 * mirror this tranche; the sweep wire reads `design.blowing[0]`, so both must move together.
 * Unlike the cove/vane/flaperon shapes (shared object refs survive `structuredClone`), the blowing
 * source stores flat copies of the disk scalars, so they need an explicit sync.
 */
function setDiskField(cfg: HighLiftAirfoil, field: 'x' | 'z' | 'diameter' | 'tiltDeg', v: number): void {
  if (!Number.isFinite(v)) return;
  cfg.propeller[field] = v;
  const first = cfg.design.blowing?.[0];
  if (first) first[field] = v;
}

function setVaneElement(
  cfg: HighLiftAirfoil,
  id: string,
  apply: (shape: NonNullable<Extract<Element, { kind: 'vane-bspline' }>['shape']>) => void,
): void {
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.id === id && el.kind === 'vane-bspline');
  if (element?.kind === 'vane-bspline') apply(element.shape);
}

function setFlaperonElement(cfg: HighLiftAirfoil, id: string, field: FlaperonElementField, v: number): void {
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.id === id && el.kind === 'flaperon-derived');
  if (element?.kind !== 'flaperon-derived') return;
  if (field === 'noseTipX') {
    element.shape.noseTip[0] = v;
  } else if (field === 'noseTipZ') {
    element.shape.noseTip[1] = v;
  } else {
    element.shape[field] = v;
  }
}

/**
 * Set the deflection (deg) of a plain-flap or plain-slat element by collection id. The deflection
 * lives ON the element (per-element rigging — the Rig table's headline edit). Materialises the
 * explicit collection first (like {@link setVaneElement}) so the legacy first-of-kind entries keep
 * pointing at the legacy fields. No-op for elements with no own deflection (main / vane / flaperon).
 */
function setElementDeflection(cfg: HighLiftAirfoil, id: string, deg: number): void {
  if (!Number.isFinite(deg)) return;
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.id === id);
  if (
    element?.kind === 'plain-flap' ||
    element?.kind === 'plain-slat' ||
    element?.kind === 'slat' ||
    element?.kind === 'krueger'
  ) {
    element.deflectionDeg = deg;
  }
}

/**
 * Set the cove depth (chords) of a plain-flap element by collection id. The cove back wall sits at
 * `hingeX`; `depth` opens the upper/lower lip cuts aft of the hinge (a slotted flap; `depth <= 0`
 * ⇒ sealed). Re-derives the element's {@link CoveCutouts} via {@link coveFromHinge} and moves the
 * aft nose's cut-x's to match (the `hingeX + depth` convention from `makePlainFlapElement`). Only
 * plain-flap carries its own cove (the slat-cove lives on the main), so this is a no-op otherwise.
 */
function setElementCoveDepth(cfg: HighLiftAirfoil, id: string, depth: number): void {
  if (!Number.isFinite(depth)) return;
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.id === id);
  if (element?.kind !== 'plain-flap') return;
  const d = depth > 0 ? depth : 0;
  element.cove = coveFromHinge(element.hingeX, d);
  element.shape.upperCutX = element.hingeX + d;
  element.shape.lowerCutX = element.hingeX + d;
}

/**
 * Set the slot depth (chords) of a PROPER slat element by collection id (the concave-cusp depth on
 * the slat underside — the slot ceiling). Materialises the explicit collection like the other
 * per-element setters. No-op for non-slat elements.
 */
function setElementSlotDepth(cfg: HighLiftAirfoil, id: string, depth: number): void {
  if (!Number.isFinite(depth)) return;
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.id === id);
  if (element?.kind === 'slat') element.slotDepth = depth > 0 ? depth : 0;
}

/**
 * Set the DROOP NOSE morph on the (first) main-cove element: `field` selects the droop angle (deg,
 * positive = nose down) or the droop hinge x (chords). A 0 angle is a no-op morph (the default config
 * stays byte-identical). Materialises the explicit collection so the main-cove entry persists.
 */
function setMainDroop(cfg: HighLiftAirfoil, field: 'droopDeg' | 'droopHingeX', v: number): void {
  if (!Number.isFinite(v)) return;
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.kind === 'main-cove');
  if (element?.kind === 'main-cove') element[field] = v;
}

/**
 * Set a Krueger element NUMERIC scalar field by collection id. Covers the originals (lower hinge x,
 * forward nose cut) and the FXC-9009 rebuild's riggable leaf scalars (panel arc fraction / camber,
 * bullnose radius, panel thickness, and the slotted-throat gap / overlap). Generic assignment over the
 * widened {@link KruegerElementField} union — every member is a `number` on {@link KruegerElement}.
 * The `variant` enum has its own setter ({@link setKruegerVariant}); it is not a member of this union.
 */
function setKruegerField(cfg: HighLiftAirfoil, id: string, field: KruegerElementField, v: number): void {
  if (!Number.isFinite(v)) return;
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.id === id);
  if (element?.kind === 'krueger') element[field] = v;
}

/**
 * Set the slot VARIANT of a Krueger element by collection id (sealed ⇄ slotted). The variant is a
 * two-state string enum (not numeric), so it can't ride {@link setKruegerField}'s numeric union.
 * Sealed (v1 default) ⇒ no through-gap; slotted opens a converging throat (the `gap` knob applies).
 * Materialises the explicit collection like the other per-element setters; no-op for non-Krueger ids.
 */
function setKruegerVariant(cfg: HighLiftAirfoil, id: string, variant: 'sealed' | 'slotted'): void {
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.id === id);
  if (element?.kind === 'krueger') element.variant = variant;
}

/**
 * Replace the MAIN element's base airfoil coordinates (the "Replace foil" op). Mutates
 * `config.main.coords` to a new {@link AirfoilCoords}; every downstream builder reads the main
 * coords through `buildConfiguration`, so the whole section (main contour, flaperon/aileron aft
 * surfaces, slat/Krueger) re-derives off the swapped foil. The element collection is foil-AGNOSTIC
 * (it indexes the legacy nested fields), so no element re-bind is needed. Deep-copied so the library
 * constant is never mutated by later edits.
 */
function setMainCoords(cfg: HighLiftAirfoil, coords: AirfoilCoords): void {
  if (!coords || !Array.isArray(coords.upper) || !Array.isArray(coords.lower)) return;
  cfg.main.coords = {
    upper: coords.upper.map((p) => [p[0], p[1]] as V2),
    lower: coords.lower.map((p) => [p[0], p[1]] as V2),
  };
}

/**
 * Set the FRISE flag on a flaperon-derived or plain-flap (aileron) element by collection id. A Frise
 * aileron grows a protruding lower-nose horn ahead of the hinge (built by `buildFlaperonWithFrise`);
 * `on === false` removes it (the contour returns to a plain nose, byte-identical). Materialises the
 * explicit collection like the other per-element setters; no-op for elements with no Frise notion.
 */
function setElementFrise(cfg: HighLiftAirfoil, id: string, on: boolean): void {
  const elements = elementsOf(cfg);
  if (!cfg.design.elements) cfg.design.elements = elements;
  const element = elements.find((el) => el.id === id);
  if (element?.kind === 'flaperon-derived' || element?.kind === 'plain-flap') {
    element.frise = on;
  }
}

function setBlowingSourceField(cfg: HighLiftAirfoil, id: string, field: BlowingSourceField, v: number): void {
  if (!Number.isFinite(v)) return;
  const sources = blowingOf(cfg);
  if (!cfg.design.blowing) cfg.design.blowing = sources;
  const index = sources.findIndex((source) => source.id === id);
  const source = sources[index];
  if (!source) return;
  source[field] = v;
  if (index === 0) cfg.propeller[field] = v;
}

/**
 * Apply a flat design+operation axis record (a Tailor suggestion's `values`) onto a `HighLiftAirfoil`
 * IN PLACE. Faithful port of the webui `thread.ts` `applyAxesToConfig` (the inverse of the server's
 * `flatten_design` / `flatten_operation`). Only keys present in `axes` are touched, so partial
 * suggestions are safe. Callers pass a fresh clone to mutate.
 */
/** Install or clear the vane follower + the Fowler deploy track it rides, on a design draft.
 *  IDENTITY (gain 1 / lag 0) clears the follower AND restores the degenerate single-pivot track,
 *  so it is byte-identical to the legacy rigid vane; any non-identity installs the follower and the
 *  non-degenerate Fowler track so the reduced gain + arc-length lag produce a real trailing slot. */
export function applyVaneFollower(cfg: HighLiftAirfoil, follower: VaneFollower): void {
  const identity = follower.gain === 1 && follower.lagChords === 0;
  if (identity) {
    cfg.design.vaneFollower = undefined;
    cfg.design.deployTrack = { pivot: [...cfg.design.deployHinge.pivot] as V2, waypoints: [] };
    cfg.design.motion = { ...PURE_ROTATION_MOTION };
    return;
  }
  cfg.design.vaneFollower = { ...follower };
  cfg.design.deployTrack = {
    pivot: [...FOLLOWER_DEPLOY_TRACK.pivot] as V2,
    waypoints: FOLLOWER_DEPLOY_TRACK.waypoints.map((w) => [...w] as V2),
  };
  cfg.design.motion = { ...FOLLOWER_MOTION };
}

export function applyAxesToConfig(cfg: HighLiftAirfoil, axes: Record<string, number>): void {
  const put = (key: string, apply: (v: number) => void): void => {
    const v = axisValue(axes, key);
    if (v !== undefined) apply(v);
  };

  // operation
  put('flap_deploy_deg', (v) => (cfg.operation.deployAngleDeg = v));
  put('flaperon_deflection_deg', (v) => (cfg.operation.flaperonAngleDeg = v));
  put('spoiler_deflection_deg', (v) => (cfg.operation.spoilerAngleDeg = v));
  put('disk_thrust_loading', (v) => (cfg.operation.diskLoading = v));
  put('freestream_speed_kt', (v) => (cfg.operation.freestreamKnots = v));
  put('density_altitude_m', (v) => (cfg.operation.densityAltitudeM = v));
  put('temperature_offset_k', (v) => (cfg.operation.tempOffsetK = v));

  // L1 main
  put('chord_m', (v) => (cfg.main.chord = v));
  put('blunt_thickness', (v) => (cfg.main.bluntThickness = v));

  // L2 design — cove
  put('cove_upper_cut_x', (v) => (cfg.design.cove.upperCutX = v));
  put('cove_lower_cut_x', (v) => (cfg.design.cove.lowerCutX = v));
  put('cove_back_x', (v) => (cfg.design.cove.coveX = v));

  // L2 design — vane
  set2(cfg.design.vane.te, 0, axisValue(axes, 'vane_te_x'));
  set2(cfg.design.vane.te, 1, axisValue(axes, 'vane_te_z'));
  cfg.design.vane.points.forEach((p, i) => {
    set2(p, 0, axisValue(axes, `vane_p${i + 1}_x`));
    set2(p, 1, axisValue(axes, `vane_p${i + 1}_z`));
  });

  // L2 design — flaperon
  put('flaperon_upper_cut_x', (v) => (cfg.design.flaperon.upperCutX = v));
  put('flaperon_lower_cut_x', (v) => (cfg.design.flaperon.lowerCutX = v));
  put('flaperon_nose_tangent_upper', (v) => (cfg.design.flaperon.noseTangentUpper = v));
  put('flaperon_nose_tangent_lower', (v) => (cfg.design.flaperon.noseTangentLower = v));
  set2(cfg.design.flaperon.noseTip, 0, axisValue(axes, 'flaperon_nose_tip_x'));
  set2(cfg.design.flaperon.noseTip, 1, axisValue(axes, 'flaperon_nose_tip_z'));

  // L2 design — hinges
  set2(cfg.design.deployHinge.pivot, 0, axisValue(axes, 'deploy_hinge_x'));
  set2(cfg.design.deployHinge.pivot, 1, axisValue(axes, 'deploy_hinge_z'));
  set2(cfg.design.flaperonHinge.pivot, 0, axisValue(axes, 'flaperon_hinge_x'));
  set2(cfg.design.flaperonHinge.pivot, 1, axisValue(axes, 'flaperon_hinge_z'));

  // L2 design — vane follower (FXC-9009). Setting either follower axis materializes the linkage
  // (seeded from the current value or the rigid identity), so a sweep/optimizer over these DOFs
  // turns the rigid vane into a follower. An all-identity result (gain 1 / lag 0) stays byte-
  // identical to the rigid assembly downstream.
  const gain = axisValue(axes, 'vane_follower_gain');
  const lag = axisValue(axes, 'vane_follower_lag');
  if (gain !== undefined || lag !== undefined) {
    const cur = cfg.design.vaneFollower ?? { gain: 1, lagChords: 0 };
    // Route through applyVaneFollower so the axes / optimizer / Tailor-sweep paths behave EXACTLY
    // like the ShapePanel sliders: an identity result (gain 1 / lag 0) COLLAPSES to no follower
    // (same absent-follower hash as the default — no cache split), and a non-identity result
    // INSTALLS the Fowler track the arc-lag rides (else lag is a silent no-op on the degenerate
    // default track, and the same axis vector would yield two different geometries by path).
    applyVaneFollower(cfg, {
      gain: gain !== undefined ? gain : cur.gain,
      lagChords: lag !== undefined ? lag : cur.lagChords,
    });
  }

  // L2 design — actuator disk (propeller). Synced onto design.blowing[0] (the sweep-wire source).
  put('disk_x', (v) => setDiskField(cfg, 'x', v));
  put('disk_z', (v) => setDiskField(cfg, 'z', v));
  put('disk_diameter', (v) => setDiskField(cfg, 'diameter', v));
  put('disk_tilt_deg', (v) => setDiskField(cfg, 'tiltDeg', v));
}

/** Live gap-metric outputs for a deployed configuration (slot-prefixed scalars Tailor optimises). */
export function gapMetricsForConfig(cfg: HighLiftAirfoil): {
  metrics: GapMetrics;
  outputs: Record<string, number>;
} {
  const conf = buildConfiguration(cfg);
  const metrics = gapMetrics({
    main: conf.main,
    vane: conf.vane,
    flaperon: conf.flaperon,
    elements: conf.elements.map((e) => ({ id: e.id, contour: e.contour })),
  });
  return { metrics, outputs: gapMetricOutputs(metrics) };
}

// ---------------------------------------------------------------------------
// NN-Newton design-opt wiring (DESIGN §8) — ranges + inverse design + optimizer
// ---------------------------------------------------------------------------
//
// A SIDECAR layer over the live design: a `Record<key,{min,max,locked}>` of design-of-experiments
// ranges (keyed by the flat `flattenDesign` axis vocabulary — the same names `applyAxesToConfig`
// writes back). It is NOT part of the config, so it never enters `designFingerprint` (exactly like
// `operation`). `buildSpecs` reads the live value of each ranged DOF out of `flattenDesign(config)`
// and pairs it with its box → `ParamSpec[]` the design-opt solvers consume. `runInverseDesign` /
// `runOptimize` bind the injected NN forward + condition + `buildConfiguration` into the generic,
// NN-free drivers and write the result back onto the unlocked DOFs.

/** A min/max box (with optional lock) on one design DOF, keyed by its flat axis name. */
export interface ParamRange {
  min: number;
  max: number;
  locked?: boolean;
}

/** Sidecar design-range map: flat-axis key → box. Absent ⇒ the DOF does not participate. */
export type RangeMap = Record<string, ParamRange>;

/** The live flowfield-NN bound into the design loops. `forward` is injectable so tests run on a
 *  synthetic Cp field without trained weights (production passes `nnInfer.forward` by default). */
export interface FoilNN {
  /** The loaded coordinate-MLP (weights fetched at runtime via the worker). */
  nn: NN;
  /** Optional NN matmul forward (default = `nnInfer.forward`); a synthetic forward in tests. */
  forward?: ForwardFn;
  /** Angle of attack (deg) fed to the NN condition. Default 0 (operation carries no α). */
  alpha?: number;
  /** Blowing term fed to the NN condition. Default = `operation.diskLoading`. */
  blow?: number;
}

/** Scalar objectives `runOptimize` can drive (all MINIMISED internally over the unlocked DOFs). */
export type FoilObjectiveKind =
  /** Maximise pressure lift (−CL). */
  | 'maxCL'
  /** Minimise pressure drag (CD). */
  | 'minCD'
  /** Maximise lift-to-drag (−CL/CD). */
  | 'maxLD'
  /** Cap peak suction — push the most-negative Cp toward 0 (shock/sep proxy). */
  | 'capSuction';

/** Result of a `runInverseDesign` call. `applied` is false (with NaN cost) when no NN / no DOFs. */
export interface InverseDesignResult {
  applied: boolean;
  finalCost: number;
  history: number[];
}

/** Result of a `runOptimize` call. `applied` is false (with NaN value) when no NN / no DOFs. */
export interface OptimizeResult {
  applied: boolean;
  finalValue: number;
  history: number[];
}

/** Result of a `runGapTarget` call. `applied` is false (NaN) when there are no free DOFs. */
export interface GapTargetResult {
  applied: boolean;
  /** The squared (metric − target) residual at the recovered design (the minimised cost). */
  finalError: number;
  /** The achieved value of the targeted gap-flow metric at the recovered design. */
  finalValue: number;
  history: number[];
}

/** Options for `runInverseDesign` (mixed-inverse region + targeted element + LM controls). */
export interface RunInverseDesignOpts {
  /** Which element's surface to match (default: the first NN element, i.e. the main). */
  element?: string;
  /** Optional [x0,x1] x/c band to confine the match to (mixed-inverse over a region). */
  region?: [number, number];
  /** Levenberg-Marquardt options forwarded to `solveInverse`. */
  lm?: LMOptions;
}

/** Options for `runOptimize` (targeted element for `capSuction` + Nelder-Mead controls). */
export interface RunOptimizeOpts {
  /** Which element the `capSuction` objective scores (default: the first NN element). */
  element?: string;
  /** Nelder-Mead options forwarded to `optimizeScalar`. */
  nm?: NelderMeadOptions;
}

/** Flat-axis value record for a spec list (key → value), the shape `applyAxesToConfig` consumes. */
function specsToAxes(specs: ParamSpec[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const s of specs) out[s.key] = s.value;
  return out;
}

/**
 * Map a deployed {@link Configuration} to the NN's named element list. The NN reads signed distance
 * to `main`/`vane`/`flap`, so the FIRST element of each canonical kind takes those names (the default
 * triple → exactly main/vane/flap, matching the training coordinate); any extra element keeps its own
 * id as the strip name (unique, never colliding with the canonical three).
 */
function nnElementsOf(configuration: Configuration): FoilElement[] {
  const named = new Set<string>();
  return configuration.elements.map((el) => {
    let name = el.id;
    if (el.kind === 'main-cove' && !named.has('main')) {
      named.add('main');
      name = 'main';
    } else if (el.kind === 'vane-bspline' && !named.has('vane')) {
      named.add('vane');
      name = 'vane';
    } else if (el.kind === 'flaperon-derived' && !named.has('flap')) {
      named.add('flap');
      name = 'flap';
    }
    return { name, contour: el.contour };
  });
}

/**
 * Build the {@link ParamSpec} list for the ranged DOFs from the LIVE design. Each spec's value is the
 * current value of that flat axis (`flattenDesign(config)[key]`); ranges whose key has no live value
 * (e.g. an axis the current config doesn't emit) are skipped. Mixed-inverse = mark a range `locked`.
 */
function buildSpecsFor(config: HighLiftAirfoil, ranges: RangeMap): ParamSpec[] {
  const flat = flattenDesign(config);
  const out: ParamSpec[] = [];
  for (const [key, r] of Object.entries(ranges)) {
    const value = flat[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    out.push({ key, value, min: r.min, max: r.max, locked: r.locked });
  }
  return out;
}

/** Write the ranged spec VALUES back onto a fresh design copy (the cpAt/objective param writeback). */
function applyParamsToDesign(design: HighLiftAirfoil, specs: ParamSpec[]): HighLiftAirfoil {
  const draft = cloneConfig(design);
  applyAxesToConfig(draft, specsToAxes(specs));
  return draft;
}

/** Result of {@link solveGapTarget}: the recovered specs + the achieved metric + applied flag. */
export interface SolveGapTargetResult {
  /** The (optimised) spec list; unlocked entries carry the recovered values to write back. */
  specs: ParamSpec[];
  /** The minimised (metric − target)² residual. */
  finalError: number;
  /** The achieved value of the targeted gap-flow metric at the recovered design. */
  finalValue: number;
  /** False when there are no movable (unlocked) ranged DOFs — nothing to solve. */
  applied: boolean;
  /** optimizeScalar cost history (the convergence sparkline). */
  history: number[];
}

/**
 * PURE gap-flow inverse solve (FXC-9009 #10): minimise (gap-flow metric − target)² over the free
 * ranged DOFs with the existing {@link optimizeScalar} harness. No NN, no React, no side effects — so
 * it is unit-testable headlessly and shared by the {@link useFoilDesign} hook's `runGapTarget`
 * (which adds only the live-config write-back of the recovered DOFs).
 */
export function solveGapTarget(
  config: HighLiftAirfoil,
  ranges: RangeMap,
  metricKey: string,
  targetValue: number,
  opts?: { nm?: NelderMeadOptions },
): SolveGapTargetResult {
  const specs = buildSpecsFor(config, ranges);
  if (specs.length === 0 || specs.every((s) => s.locked)) {
    return { specs, finalError: NaN, finalValue: NaN, applied: false, history: [] };
  }
  const metricAt = (design: HighLiftAirfoil): number =>
    gapMetricOutputs(gapMetrics(buildConfiguration(design)))[metricKey] ?? NaN;
  // Minimise (metric − target)² over the full design vector (spec order); mirrors runOptimize's
  // unpack convention. A non-finite metric (a degenerate/self-intersecting contour) is a flat penalty
  // strictly larger than any reachable finite residual, so the optimiser never prefers it.
  const scalar = (vec: number[]): number => {
    const v = metricAt(applyParamsToDesign(config, unpackFullVector(specs, vec)));
    if (!Number.isFinite(v)) return 1e9;
    const d = v - targetValue;
    return d * d;
  };
  const res = optimizeScalar({ specs, objective: scalar, opts: opts?.nm });
  return {
    specs: res.specs,
    finalError: res.finalValue,
    finalValue: metricAt(applyParamsToDesign(config, res.specs)),
    applied: true,
    history: res.history,
  };
}

// ---------------------------------------------------------------------------
// Geometric flap-deployment optimizer (FXC-9009 — "optimise clearance/deflection/path/sizes from
// geometry + first-principles flow angles, no NN/CFD"). Distinct from `solveGapTarget` (which drives
// ONE named metric to a target): this MINIMISES a composite first-principles health score over the
// deployment's own knobs (deflection, hinge position = path/overlap/gap, nose camber, cove depth).
// ---------------------------------------------------------------------------

/** The deployment DOFs this optimizer is allowed to move: Operation (deflection) + Geometry
 *  (hinge position = the deploy PATH's overlap/gap, nose camber, cove/slot depth = SIZES) knob-
 *  registry keys. Condition knobs (α/Mach/Re) are flow state, not design, and excluded.
 *  MAINTENANCE: this list is a hardcoded subset of `KNOB_REGISTRY`, not derived from it — adding a
 *  new Operation/Geometry knob to the registry does NOT automatically make it available here. If you
 *  add a knob that's a genuine deployment DOF (affects the deployed slot geometry), add its key here
 *  too, or "optimize deployment" will silently ignore it. */
const GEOMETRIC_OPTIMIZE_KEYS = [
  'flap_deploy_deg',
  'flaperon_deflection_deg',
  'deploy_hinge_x',
  'deploy_hinge_z',
  'flaperon_nose_tip_z',
  'cove_back_x',
  // Vane follower linkage — genuine deployed-slot DOFs (gain + track lag reshape the vane's
  // deployed pose), so the deployment optimizer may move them (FXC-9009 double-slotted tranche).
  'vane_follower_gain',
  'vane_follower_lag',
] as const;

/** Build the optimizer's `ParamSpec[]` straight from the knob registry (current value + the SAME
 *  effective bounds / fixed-free state the Design page's limits panel already shows) — so a run
 *  respects whatever the user has already locked, rather than introducing a second, separate range
 *  sidecar to configure. `buildSpecsFor`/`RangeMap` (what `runGapTarget`/`runOptimize` use) sources
 *  values via `flattenDesign(config)[key]`, and `flattenDesign` DELIBERATELY EXCLUDES Operation
 *  (deploy/flaperon angle — see its own module comment: "reshaping the foil changes the design
 *  fingerprint; nudging the operating point does not"), so `flap_deploy_deg`/`flaperon_deflection_deg`
 *  could never come from that mechanism regardless. `deploy_hinge_x/z`/`flaperon_nose_tip_z`/
 *  `cove_back_x` ARE in `flattenDesign` and could have used `RangeMap`, but reading the registry +
 *  `applyAxesToConfig` directly keeps all 6 deployment DOFs on ONE mechanism instead of splitting
 *  Operation across the registry and Geometry across `RangeMap`. */
function buildGeometricOptimizeSpecs(config: HighLiftAirfoil): ParamSpec[] {
  const ctx: KnobCtx = { alphaDeg: 0, flowConditions: { mach: 0.2, reynolds: 1e6 }, config };
  const out: ParamSpec[] = [];
  for (const key of GEOMETRIC_OPTIMIZE_KEYS) {
    const def = KNOB_BY_KEY.get(key);
    if (!def) continue;
    const c = config.constraints?.[key];
    const [min, max] = knobBounds(def, c);
    out.push({ key, value: def.read(ctx), min, max, locked: c?.fixed === true });
  }
  return out;
}

/** Write the spec values back onto a fresh design copy via the SAME axis-application `applyAxesToConfig`
 *  already used by the Sweep/DOE + Rig-table writeback paths. */
function applyGeometricOptimizeSpecs(config: HighLiftAirfoil, specs: ParamSpec[]): HighLiftAirfoil {
  const draft = cloneConfig(config);
  applyAxesToConfig(draft, specsToAxes(specs));
  return draft;
}

/**
 * First-principles slot-shape penalty (lower = healthier), pure geometry, no flow solve:
 *  - convergence: slot-exit/slot-throat ratio. <1 is a converging (accelerating, separation-
 *    resistant) channel — the textbook high-lift slot design intent (see `gapMetrics.ts`); penalize
 *    quadratically once it creeps toward/past parity (diffusing).
 *  - reattachOffset: signed offset of the downstream nose from the upstream wake line. <=0 means the
 *    nose sits IN the upstream shear layer (reattachment-favorable); penalize it sitting exposed above
 *    the wake, scaled up since this is chord-normalized and typically small.
 *  - noseCurvature: an extremely sharp downstream nose tolerates the slot jet's incidence worse; a
 *    soft, generously-thresholded penalty (element shape is mostly fixed by the base foil, so this
 *    term mainly discourages the optimizer from drifting a movable nose-camber DOF to an extreme).
 * Weights/thresholds are heuristic (this is a fast geometric proxy, not a flow solve) — documented so
 * they can be retuned against real RANS/Tailor data later without touching the optimizer plumbing.
 */
export function slotShapeCost(m: SlotMetrics): number {
  const convergencePenalty = Math.max(0, m.convergence - 0.9) ** 2 * 4;
  const reattachPenalty = Math.max(0, m.reattachOffset) ** 2 * 400;
  const curvaturePenalty = Math.max(0, m.noseCurvature - 60) ** 2 * 1e-4;
  return convergencePenalty + reattachPenalty + curvaturePenalty;
}

/**
 * Clearance penalty swept across the FULL deploy/flaperon motion envelope (not just one operating
 * point) — reusing {@link motionGapCheck}'s existing sweep, at a MUCH coarser grid than the workbench
 * tile's 9x3 (3x1 = 3deploy samples × the flaperon range's low end only). This objective is evaluated
 * on the order of hundreds of times per optimizer run (Nelder-Mead in a 6-D box), and each sample
 * re-derives geometry + an O(n²) vertex-to-edge clearance check per slot, so keeping the inner-loop
 * sweep cheap is what keeps the optimizer sub-second instead of freezing the tab for several seconds
 * (measured: a 5x2 sweep here made 200 iterations time out past 5s). Collision anywhere in the coarse
 * envelope is a large flat penalty (effectively a hard constraint under Nelder-Mead); choking is
 * graded by how far under the warn band it falls; and every slot's mid-envelope gap
 * `(minGap+maxGap)/2` is pulled toward the ~1-2.5%c band `motionGapCheck.ts` already documents as
 * typical for high-lift slots. A full 9x3 `motionGapCheck` still runs once, cheaply, on the FINAL
 * result for the reported/displayed health (see the Slot Health tile), so accuracy there is unaffected.
 */
function clearanceCost(cfg: HighLiftAirfoil): number {
  const swept = motionGapCheck(cfg, { deploySteps: 3, flaperonSteps: 1 });
  let cost = 0;
  const bandLo = 0.010;
  const bandHi = 0.025;
  for (const s of swept.slots) {
    if (s.collides) cost += 1000;
    else if (s.choked) cost += 50 + (0.005 - s.minGap) * 2000;
    const mid = (s.minGap + s.maxGap) / 2;
    if (mid < bandLo) cost += ((bandLo - mid) * 200) ** 2;
    else if (mid > bandHi) cost += ((mid - bandHi) * 100) ** 2;
  }
  return cost;
}

/** Bonus (subtracted from cost) per unit of deploy-angle FRACTION of its own knob range. A high-lift
 *  flap's whole purpose is lift, which grows with deployment — so "deploy as little as possible" is
 *  not a real optimum even though it trivially minimises separation risk. This term is what makes
 *  more deployment genuinely desirable, so the optimizer finds the LARGEST deploy angle the slot-shape
 *  and clearance terms will tolerate, rather than degenerately retracting. Scaled against the
 *  slot-shape penalty's own typical magnitude (tens, per `slotShapeCost`'s weights) so it is CAPABLE of
 *  being outweighed by a real violation, rather than always trivially winning regardless of shape
 *  health. NOTE (verified 2026-07-01): for the DEFAULT geometry within the registry's default bounds,
 *  deploying fully does not currently trip the shape/clearance penalties at all — retracted (near
 *  deployAngleDeg=0) is actually a degenerate near-overlapping "slot" that scores WORSE on convergence/
 *  reattachment than a properly deployed one, so every term agrees max deployment is best here. That's
 *  a fact about this specific shape + bounds, not evidence the penalty terms are inert — see the
 *  `slotShapeCost` describe block in `useFoilDesign.test.tsx`, which drives the function directly
 *  with synthetic bad SlotMetrics to prove it penalizes real violations regardless of what the
 *  current default shape happens to trigger. */
const DEPLOY_REWARD_WEIGHT = 8;

/**
 * Composite objective `solveGeometricOptimize` minimises: swept clearance health (a range-of-motion
 * CONSTRAINT — independent of the candidate's chosen deploy angle, since the mechanism must never
 * collide anywhere it can physically reach) + slot-shape health AT THE CANDIDATE'S OWN operating point
 * (so deflection genuinely trades off against flow health — more deploy angle changes the deployed
 * geometry and typically worsens reattachment/convergence, a real effect since `buildConfiguration`
 * uses `cfg.operation.deployAngleDeg`) − a reward for deploying further (see {@link DEPLOY_REWARD_WEIGHT}).
 * Earlier drafts evaluated shape cost at a HARDCODED full-deploy point regardless of the candidate's
 * own `deployAngleDeg`, which made that DOF invisible to the objective (Nelder-Mead had zero gradient
 * on it) — fixed by evaluating shape at `cfg` as given.
 */
function geometricHealthCost(cfg: HighLiftAirfoil): number {
  const clearance = clearanceCost(cfg);
  const conf = buildConfiguration(cfg);
  const metrics = gapMetrics({
    main: conf.main,
    vane: conf.vane,
    flaperon: conf.flaperon,
    elements: conf.elements.map((e) => ({ id: e.id, contour: e.contour })),
  });
  const shape = metrics.slots.reduce((sum, s) => sum + slotShapeCost(s.metrics), 0);
  const deployDef = KNOB_BY_KEY.get('flap_deploy_deg');
  const [deployLo, deployHi] = deployDef ? knobBounds(deployDef, cfg.constraints?.['flap_deploy_deg']) : [0, 1];
  const span = Math.max(1e-9, deployHi - deployLo);
  const deployFrac = (cfg.operation.deployAngleDeg - deployLo) / span;
  const deployReward = -DEPLOY_REWARD_WEIGHT * deployFrac;
  return clearance + shape + deployReward;
}

/** Result of a `runGeometricOptimize` call. `applied` is false (NaN cost) when every deployment DOF
 *  is locked (nothing free to move). */
export interface GeometricOptimizeResult {
  applied: boolean;
  /** The recovered spec list (unlocked entries carry the optimized values to write back). */
  specs: ParamSpec[];
  /** The composite health cost (lower = healthier) at the recovered design. */
  finalCost: number;
  /** The health cost BEFORE optimizing, for a before/after readout. */
  initialCost: number;
  history: number[];
}

/**
 * PURE geometric optimizer for the flap deployment (FXC-9009): minimises {@link geometricHealthCost}
 * — swept clearance + first-principles slot-shape criteria — over the deployment's own knobs
 * (deflection, hinge position, nose camber, cove depth), respecting whatever the Design page's
 * fixed/free + limits panel already set. No NN, no backend, no side effects — unit-testable headlessly
 * and shared by {@link useFoilDesign}'s `runGeometricOptimize` (which adds only the live write-back).
 */
export function solveGeometricOptimize(
  config: HighLiftAirfoil,
  opts?: { nm?: NelderMeadOptions },
): GeometricOptimizeResult {
  const specs = buildGeometricOptimizeSpecs(config);
  const initialCost = geometricHealthCost(config);
  if (specs.length === 0 || specs.every((s) => s.locked)) {
    return { applied: false, specs, finalCost: NaN, initialCost, history: [] };
  }
  const scalar = (vec: number[]): number => {
    const candidate = applyGeometricOptimizeSpecs(config, unpackFullVector(specs, vec));
    return geometricHealthCost(candidate);
  };
  // Default maxIter is much lower than optimizeScalar's own default (400): that default was tuned for
  // cheap NN-forward objectives, not this multi-sample geometry-sweep one. 80 iterations in the 6-D box
  // (a few hundred evaluations) already converges the default config's cost by >30x in testing while
  // staying comfortably sub-second; callers can still override via `opts.nm.maxIter`.
  const res = optimizeScalar({ specs, objective: scalar, opts: { maxIter: 80, ...opts?.nm } });
  return { applied: true, specs: res.specs, finalCost: res.finalValue, initialCost, history: res.history };
}

/** NN condition for the live design: α/blow from the {@link FoilNN}, Mach/Re from the flow state. */
function nnCondFor(config: HighLiftAirfoil, flow: FlowConditions, model: FoilNN): NNCond {
  return {
    alpha: model.alpha ?? 0,
    mach: flow.mach,
    re: flow.reynolds,
    blow: model.blow ?? config.operation.diskLoading,
  };
}

/** Setters the controls panel binds to. Each takes a numeric value and updates the live config. */
export interface FoilSetters {
  // operation (flat operating-point axes)
  setDeployDeg: (v: number) => void;
  setFlaperonDeg: (v: number) => void;
  setSpoilerDeg: (v: number) => void;
  setDiskLoading: (v: number) => void;
  setFreestreamKt: (v: number) => void;
  setDensityAltitudeM: (v: number) => void;
  setTempOffsetK: (v: number) => void;
  // L1 main
  setChordM: (v: number) => void;
  setBluntThickness: (v: number) => void;
  // L2 design — cove
  setCoveUpperCutX: (v: number) => void;
  setCoveLowerCutX: (v: number) => void;
  setCoveBackX: (v: number) => void;
  // L2 design — deploy hinge
  setDeployHingeX: (v: number) => void;
  setDeployHingeZ: (v: number) => void;
  // L2 design — vane follower linkage (FXC-9009 double-slotted tranche)
  setVaneFollowerGain: (v: number) => void;
  setVaneFollowerLag: (v: number) => void;
  // L2 design — flaperon hinge
  setFlaperonHingeX: (v: number) => void;
  setFlaperonHingeZ: (v: number) => void;
  // L2 design — flaperon shape
  setFlaperonUpperCutX: (v: number) => void;
  setFlaperonLowerCutX: (v: number) => void;
  setFlaperonNoseTipX: (v: number) => void;
  setFlaperonNoseTipZ: (v: number) => void;
  // L2 design — vane control points
  setVaneTeX: (v: number) => void;
  setVaneTeZ: (v: number) => void;
  /** Set vane control point i (0-based into `design.vane.points`), coordinate 0=x / 1=z. */
  setVanePoint: (i: number, coord: 0 | 1, v: number) => void;
  /** Set a vane element field by collection id, including added vane rows. */
  setVaneElementTe: (id: string, coord: 0 | 1, v: number) => void;
  /** Set a vane element control point by collection id, including added vane rows. */
  setVaneElementPoint: (id: string, i: number, coord: 0 | 1, v: number) => void;
  /** Set a flaperon-derived element field by collection id. */
  setFlaperonElementField: (id: string, field: FlaperonElementField, v: number) => void;
  /** Set the deflection (deg) of a plain-flap / plain-slat element by collection id (Rig table). */
  setElementDeflection: (id: string, deg: number) => void;
  /** Set the cove depth (chords) of a plain-flap element by collection id (Rig table). */
  setElementCoveDepth: (id: string, depth: number) => void;
  /** Set the slot depth (chords) of a PROPER slat element by collection id (Rig table cove column). */
  setElementSlotDepth: (id: string, depth: number) => void;
  /** Set a Krueger element NUMERIC scalar field (lower hinge / nose cut / panel / gap / overlap) by id. */
  setKruegerField: (id: string, field: KruegerElementField, v: number) => void;
  /** Set a Krueger element's slot variant (sealed ⇄ slotted) by collection id (Rig table toggle). */
  setKruegerVariant: (id: string, variant: 'sealed' | 'slotted') => void;
  /** Set the droop-nose morph on the main: droop angle (deg, +=nose down) or droop hinge x (chords). */
  setMainDroop: (field: 'droopDeg' | 'droopHingeX', v: number) => void;
  /** Replace the MAIN element's base airfoil coordinates (the "Replace foil" op). */
  setMainCoords: (coords: AirfoilCoords) => void;
  /** Toggle the FRISE variant on a flaperon-derived / plain-flap (aileron) element by collection id. */
  setElementFrise: (id: string, on: boolean) => void;
  // L2 design — actuator disk
  setDiskX: (v: number) => void;
  setDiskZ: (v: number) => void;
  setDiskDiameter: (v: number) => void;
  setDiskTiltDeg: (v: number) => void;
  /** Set a blowing source field by collection id, including added blowing rows. */
  setBlowingSourceField: (id: string, field: BlowingSourceField, v: number) => void;
  /**
   * Unified setter for a dragged canvas handle. `worldX`/`worldZ` are in STOWED config coords
   * (the caller inverts the deploy transform first), and are routed to the right config path
   * for the handle. Unknown handle ids are a no-op.
   */
  setControlPoint: (handle: FoilHandleId, worldX: number, worldZ: number) => void;
}

export interface UseFoilDesign {
  /** The live config (immutable per render; replaced on every edit). */
  config: HighLiftAirfoil;
  /** Typed setters for the flat axis schema + key design axes. */
  setters: FoilSetters;
  /** Apply a flat Tailor axis record (partial allowed) onto the live config. */
  applyAxes: (axes: Record<string, number>) => void;
  /** FXC-9009 phase 2: set a knob's fixed/free + limits (partial), keyed by knob key (knobRegistry). */
  setKnobConstraint: (key: string, partial: Partial<{ fixed: boolean; min: number; max: number }>) => void;
  /** The data-driven control-point handles for the live config (AC-H) — one per element control point. */
  handleDescriptors: FoilHandleDescriptor[];
  /**
   * Apply a dragged handle's STOWED position onto the live config via its descriptor's `set` closure.
   * `worldX`/`worldZ` are in stowed config coords (the canvas inverts the deploy transform first).
   * Unknown handle ids are a no-op.
   */
  setHandleDescriptor: (handleId: string, worldX: number, worldZ: number) => void;
  /** Replace the entire config (e.g. an agent `load_design`). Does NOT re-baseline (use {@link loadConfig}). */
  setConfig: (cfg: HighLiftAirfoil) => void;
  /**
   * Load a saved/committed geometry as the live BUFFER **and** RE-BASELINE to it (XFOIL "current" ←
   * loaded foil). The buffer becomes clean (`isDirty === false`) against the loaded shape, and
   * {@link baseline} points at that saved node so reshaping diverges from it.
   */
  loadConfig: (cfg: HighLiftAirfoil, ref: { name: string; slug: string; nodeId?: string | null }) => void;
  /**
   * Promote the current BUFFER into the BASELINE (XFOIL PANE/EXEC). Call AFTER the commit has written
   * the variant geometry node so the new baseline references it; the buffer becomes clean against the
   * just-committed shape. The committed `config` is the live one, so no config replacement happens.
   */
  commitBaseline: (ref: { name: string; slug: string; nodeId?: string | null }) => void;
  /** The baseline ("current") geometry the buffer is diffed against (seed / loaded / committed). */
  baseline: BaselineRef;
  /** True when the buffer's DESIGN has diverged from the baseline (a reshape happened; needs commit). */
  isDirty: boolean;
  /** The live buffer's design fingerprint (stable display id; mirrors the server's `design_hash` inputs). */
  bufferFingerprint: string;
  /** Reset to the seed default. */
  reset: () => void;
  /** The canonical element collection (for the element-list UI). */
  elements: Element[];
  /** The canonical blowing-source collection. */
  blowing: BlowingSource[];
  /** Add a free-form vane element (duplicates + offsets the primary vane). */
  addVane: () => void;
  /** Add a plain flap (aileron): a TE control surface cut from the main with a cove + gap. */
  addPlainFlap: () => void;
  /** Add a leading-edge slat: the fore mirror of a plain flap (the main's nose split off + drooped). */
  addSlat: () => void;
  /** Add a Krueger flap: a lower-LE panel that folds down+forward from under the nose. */
  addKrueger: () => void;
  /** Compose the native slotted-flap system (open cove + vane + slotted flaperon + deploy rig). */
  addSlottedFlap: () => void;
  /** Compose the SIMPLE slotted flap (open cove + flap, no vane). */
  addSingleSlottedFlap: () => void;
  /** Add an actuator-disk blowing source. */
  addBlowing: () => void;
  /** Remove an added element / blowing source by id (default-section entries are kept). */
  removeCollectionItem: (id: string) => void;
  /** Deployed element contours + control points for the current operating point. */
  configuration: Configuration;
  /** Gap-flow / reattachment metrics (both slots) for the deployed configuration. */
  gapMetrics: GapMetrics;
  /** Flattened gap-metric outputs (slot-prefixed scalars). */
  gapMetricOutputs: Record<string, number>;
  /** Gap check across the section's range of motion (per-slot min gap + interference) — FXC-9009 #8. */
  motionGap: MotionGapCheck;
  /** RANS sweep inputs assembled from the design (the same payload the iframe POSTed). */
  sweepInputs: SweepInputs;
  /** ISA freestream → Re / Mach for the operating point (the Operation read-out). */
  flowConditions: FlowConditions;

  // ---- NN-Newton design-opt (DESIGN §8) ---------------------------------------------------------
  /** Sidecar design-range map (flat-axis key → box). NOT in the config ⇒ excluded from the fingerprint. */
  ranges: RangeMap;
  /** Set (or replace) the range box on one DOF (keyed by its flat axis name). */
  setRange: (key: string, range: ParamRange) => void;
  /** Remove the range box on one DOF. */
  clearRange: (key: string) => void;
  /** Build the {@link ParamSpec} list for the currently-ranged DOFs from the live design. */
  buildSpecs: () => ParamSpec[];
  /**
   * Bind (or clear) the flowfield-NN used by the design loops + the predicted `surfaceCp`. The NN
   * weights are fetched at runtime (the worker), so this is how the loaded model enters the hook;
   * pass `null` to clear. With no NN bound, the inverse-design / optimize loops no-op gracefully.
   */
  setNN: (model: FoilNN | null) => void;
  /** True when an NN is bound (the design loops + Cp plot are live). */
  hasNN: boolean;
  /**
   * Predicted per-element surface Cp from the bound NN (suction/pressure strips) for the live
   * configuration + operating point, or `null` when no NN is bound (honest-absent, never fabricated).
   */
  surfaceCp: SurfaceCpElement[] | null;
  /**
   * Run NN-Newton target-Cp inverse design (mixed-inverse over the unlocked ranged DOFs) and APPLY
   * the recovered design back onto the live config. Resolves `{applied:false}` when no NN is bound or
   * no DOFs are ranged.
   */
  runInverseDesign: (target: SurfaceCp, opts?: RunInverseDesignOpts) => Promise<InverseDesignResult>;
  /**
   * Run scalar-objective optimisation (Nelder-Mead) over the unlocked ranged DOFs and APPLY the
   * minimiser back onto the live config. Resolves `{applied:false}` when no NN / no DOFs.
   */
  runOptimize: (objective: FoilObjectiveKind, opts?: RunOptimizeOpts) => Promise<OptimizeResult>;
  /** Inverse-design a gap-flow slot metric to a target value over the free DOFs (pure geometry, no NN). */
  runGapTarget: (
    metricKey: string,
    targetValue: number,
    opts?: { nm?: NelderMeadOptions },
  ) => Promise<GapTargetResult>;
  /** PURE geometric optimizer for the flap deployment: minimises swept clearance + first-principles
   *  slot-shape health over the deployment's own knobs (deflection/hinge-position/nose-camber/cove
   *  depth), respecting the Design page's fixed/free + limits. No NN, no backend. */
  runGeometricOptimize: (opts?: { nm?: NelderMeadOptions }) => Promise<GeometricOptimizeResult>;
}

/**
 * Own the live high-lift design. Seeds `DEFAULT_HIGH_LIFT_AIRFOIL`; exposes the config, the flat
 * setters, `applyAxes`, and the memoised geometry / gap-metric / sweep / flow derivations.
 *
 * @param seed optional initial config (e.g. a Tailor suggestion already applied). Defaults to the
 *             reference geometry. Cloned so the shared module constant is never mutated.
 */
export function useFoilDesign(seed: HighLiftAirfoil = DEFAULT_HIGH_LIFT_AIRFOIL): UseFoilDesign {
  // Migrate the seed: a saved / Tailor / cached config may be the legacy shape (no element
  // collection). `migrateConfig` is idempotent + deep-clones, so the default seed passes through.
  const [config, setConfigState] = useState<HighLiftAirfoil>(() => migrateConfig(seed));

  // Baseline ("current") the buffer is diffed against. Seeds to the migrated initial config; loading
  // or committing a geometry re-baselines to that shape (see `loadConfig`/`commitBaseline`).
  const [baseline, setBaseline] = useState<BaselineRef>(() => seedBaseline(migrateConfig(seed)));

  /** Apply a structural mutation to a fresh clone, then commit it as the new config. */
  const mutate = useCallback((fn: (draft: HighLiftAirfoil) => void): void => {
    setConfigState((prev) => {
      const draft = cloneConfig(prev);
      fn(draft);
      return draft;
    });
  }, []);

  const setConfig = useCallback((cfg: HighLiftAirfoil): void => {
    // Migrate on the way in (an agent load_design / loaded saved node may be legacy-shaped).
    setConfigState(migrateConfig(cfg));
  }, []);

  // Load a saved/committed geometry as the buffer AND re-baseline to it (the buffer becomes clean).
  const loadConfig = useCallback(
    (cfg: HighLiftAirfoil, ref: { name: string; slug: string; nodeId?: string | null }): void => {
      const migrated = migrateConfig(cfg);
      setConfigState(migrated);
      setBaseline({
        source: 'loaded',
        name: ref.name,
        slug: ref.slug,
        nodeId: ref.nodeId ?? null,
        fingerprint: designFingerprint(migrated),
      });
    },
    [],
  );

  // Promote the live buffer into the baseline (after the variant node was committed): the buffer is
  // now clean against the committed shape. The config is unchanged — we only re-point the baseline.
  const commitBaseline = useCallback(
    (ref: { name: string; slug: string; nodeId?: string | null }): void => {
      setConfigState((current) => {
        setBaseline({
          source: 'committed',
          name: ref.name,
          slug: ref.slug,
          nodeId: ref.nodeId ?? null,
          fingerprint: designFingerprint(current),
        });
        return current;
      });
    },
    [],
  );

  const reset = useCallback((): void => {
    const fresh = cloneConfig(DEFAULT_HIGH_LIFT_AIRFOIL);
    setConfigState(fresh);
    setBaseline(seedBaseline(fresh));
  }, []);

  const applyAxes = useCallback(
    (axes: Record<string, number>): void => {
      mutate((draft) => {
        // FXC-9009 phase 2: honour per-knob constraints on a Tailor/applied axis record —
        // DROP fixed knobs (held at their current value) and CLAMP free knobs to their limits.
        // The applied config respects the limits even though the Tailor queue may show the raw
        // suggestion (honest: no silent server round-trip).
        const cons = draft.constraints;
        const effective: Record<string, number> = {};
        for (const [k, v] of Object.entries(axes)) {
          const c = cons?.[k];
          if (c?.fixed) continue;
          const def = KNOB_BY_KEY.get(k);
          let val = def ? clampToKnob(def, c, v) : v;
          if (!def) {
            if (c?.min != null) val = Math.max(c.min, val);
            if (c?.max != null) val = Math.min(c.max, val);
          }
          effective[k] = val;
        }
        applyAxesToConfig(draft, effective);
      });
    },
    [mutate],
  );

  /** FXC-9009 phase 2: set a knob's fixed/free + limits (partial update), keyed by knob key. */
  const setKnobConstraint = useCallback(
    (key: string, partial: Partial<{ fixed: boolean; min: number; max: number }>): void => {
      mutate((d) => {
        const cur = d.constraints?.[key] ?? { fixed: false };
        const next = { ...cur, ...partial };
        if (typeof next.min !== 'number' || !Number.isFinite(next.min)) delete next.min;
        if (typeof next.max !== 'number' || !Number.isFinite(next.max)) delete next.max;
        d.constraints = { ...(d.constraints ?? {}), [key]: next };
      });
    },
    [mutate],
  );

  // ---- element / blowing collection edits (FXC-9009 §B.5 — add/move foils & blowing) -------------
  // Materialise the explicit collection (so the legacy first-of-kind entries keep pointing at the
  // legacy fields the sliders edit), then push/splice the extra entry.
  const addVane = useCallback((): void => {
    mutate((d) => {
      const els = elementsOf(d).slice();
      const vanes = els.filter((e) => e.kind === 'vane-bspline');
      const seed = vanes[vanes.length - 1]?.shape ?? d.design.vane;
      const ordinal = vanes.length;
      els.push(makeVaneElement(seed, ordinal, els.map((e) => e.id)));
      d.design.elements = els;
    });
  }, [mutate]);

  // Add a plain flap (aileron) / leading-edge slat (FXC-9009 control-surface tranche). The factory
  // materialises the explicit collection and pushes the new element; the flap carries its own cove +
  // deflection, so it re-cuts the main + deflects without touching the legacy main-cove fields.
  const addPlainFlap = useCallback((): void => {
    mutate((d) => {
      addPlainFlapToDesign(d.design, d.main?.coords ?? DEFAULT_HIGH_LIFT_AIRFOIL.main.coords);
    });
  }, [mutate]);

  const addSlat = useCallback((): void => {
    mutate((d) => {
      addSlatToDesign(d.design, d.main?.coords ?? DEFAULT_HIGH_LIFT_AIRFOIL.main.coords);
    });
  }, [mutate]);

  const addKrueger = useCallback((): void => {
    mutate((d) => {
      addKruegerToDesign(d.design, d.main?.coords ?? DEFAULT_HIGH_LIFT_AIRFOIL.main.coords);
    });
  }, [mutate]);

  // Compose the full native slotted-flap system (open cove + vane + slotted flaperon + deploy rig)
  // copied from the DEFAULT design onto the current section — the one-click path to build a
  // Qiqi-style multi-element device on a CS-seeded (clean / aileron) section that has no cove yet.
  const addSlottedFlap = useCallback((): void => {
    mutate((d) => {
      addSlottedFlapSystemToDesign(
        d.design,
        d.main?.coords ?? DEFAULT_HIGH_LIFT_AIRFOIL.main.coords,
        DEFAULT_HIGH_LIFT_AIRFOIL.design,
        // fit the copied system onto THIS section (slot-frame map + clearance
        // tune) instead of riding the default's absolute coordinates
        DEFAULT_HIGH_LIFT_AIRFOIL.main.coords,
      );
    });
  }, [mutate]);

  // The SIMPLE slotted flap: open cove + flap, no vane - fitted the same way.
  const addSingleSlottedFlap = useCallback((): void => {
    mutate((d) => {
      addSingleSlottedFlapToDesign(
        d.design,
        d.main?.coords ?? DEFAULT_HIGH_LIFT_AIRFOIL.main.coords,
        DEFAULT_HIGH_LIFT_AIRFOIL.design,
        DEFAULT_HIGH_LIFT_AIRFOIL.main.coords,
      );
    });
  }, [mutate]);

  const addBlowing = useCallback((): void => {
    mutate((d) => {
      const sources = blowingOf(d).slice();
      sources.push(
        makeBlowingSource(
          sources[sources.length - 1] ?? sources[0]!,
          sources.length,
          sources.map((s) => s.id),
        ),
      );
      d.design.blowing = sources;
    });
  }, [mutate]);

  // Remove an added (non-default) element or blowing source. Default-section entries (main / the
  // primary vane, flaperon, propeller) are kept so the legacy sliders/handles never orphan.
  const removeCollectionItem = useCallback((id: string): void => {
    if (isDefaultCollectionId(id)) return;
    mutate((d) => {
      const els = elementsOf(d);
      if (els.some((e) => e.id === id)) {
        d.design.elements = els.filter((e) => e.id !== id);
        return;
      }
      const sources = blowingOf(d);
      if (sources.some((s) => s.id === id)) {
        d.design.blowing = sources.filter((s) => s.id !== id);
      }
    });
  }, [mutate]);

  // ---- undo/redo wiring -------------------------------------------------------------------------
  // True while we're applying a restored snapshot, so the config-change effect doesn't echo a new
  // history entry back into the store (which would invalidate the redo stack).
  const restoringRef = useRef(false);
  // Skip pushing for the very first config (the initial snapshot is pushed on mount registration).
  const didInitialPushRef = useRef(false);
  // Subscribe to the store's pendingRestore so the consume effect re-runs when a restore is staged.
  const pendingRestore = useUndoRedoStore((s) => s.pendingRestore);

  // Register the scope on mount (becomes active) + seed the history with the initial snapshot;
  // unregister on unmount to clear its history + activeScope.
  useEffect(() => {
    const store = useUndoRedoStore.getState();
    store.registerScope(FOIL_UNDO_SCOPE);
    store.push(FOIL_UNDO_SCOPE, config, { coalesceMs: 0 });
    didInitialPushRef.current = true;
    return () => {
      useUndoRedoStore.getState().unregisterScope(FOIL_UNDO_SCOPE);
    };
    // Mount-only: the initial snapshot is the seed config; later changes push via the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push a coalesced snapshot on every config change, EXCEPT the initial config and changes that
  // came from a restore (guarded by restoringRef). Coalescing collapses a continuous drag into a
  // single undo step.
  useEffect(() => {
    if (!didInitialPushRef.current) return; // initial push handled on mount
    if (restoringRef.current) {
      restoringRef.current = false;
      return;
    }
    useUndoRedoStore.getState().push(FOIL_UNDO_SCOPE, config, { coalesceMs: 400 });
  }, [config]);

  // When undo/redo stages a snapshot for this scope, consume it and apply WITHOUT re-pushing.
  useEffect(() => {
    if (!pendingRestore || pendingRestore.scope !== FOIL_UNDO_SCOPE) return;
    const snapshot = useUndoRedoStore.getState().consumeRestore(FOIL_UNDO_SCOPE);
    if (!snapshot) return;
    restoringRef.current = true;
    setConfigState(cloneConfig(snapshot as HighLiftAirfoil));
  }, [pendingRestore]);

  const setters = useMemo<FoilSetters>(
    () => ({
      setDeployDeg: (v) => mutate((d) => (d.operation.deployAngleDeg = v)),
      setFlaperonDeg: (v) => mutate((d) => (d.operation.flaperonAngleDeg = v)),
      setSpoilerDeg: (v) => mutate((d) => (d.operation.spoilerAngleDeg = v)),
      setDiskLoading: (v) => mutate((d) => (d.operation.diskLoading = v)),
      setFreestreamKt: (v) => mutate((d) => (d.operation.freestreamKnots = v)),
      setDensityAltitudeM: (v) => mutate((d) => (d.operation.densityAltitudeM = v)),
      setTempOffsetK: (v) => mutate((d) => (d.operation.tempOffsetK = v)),

      setChordM: (v) => mutate((d) => (d.main.chord = v)),
      setBluntThickness: (v) => mutate((d) => (d.main.bluntThickness = v)),

      setCoveUpperCutX: (v) => mutate((d) => (d.design.cove.upperCutX = v)),
      setCoveLowerCutX: (v) => mutate((d) => (d.design.cove.lowerCutX = v)),
      setCoveBackX: (v) => mutate((d) => (d.design.cove.coveX = v)),

      setDeployHingeX: (v) => mutate((d) => (d.design.deployHinge.pivot[0] = v)),
      setDeployHingeZ: (v) => mutate((d) => (d.design.deployHinge.pivot[1] = v)),

      // Vane follower: moving gain below 1 or lag above 0 turns the rigid vane into a
      // FOLLOWER and installs the non-degenerate Fowler deploy track it rides (so the lag +
      // slot are real); returning both to identity removes the follower and restores the
      // degenerate single-pivot track — so identity stays byte-identical to the legacy vane.
      setVaneFollowerGain: (v) => mutate((d) => applyVaneFollower(d,
        { gain: v, lagChords: d.design.vaneFollower?.lagChords ?? 0 })),
      setVaneFollowerLag: (v) => mutate((d) => applyVaneFollower(d,
        { gain: d.design.vaneFollower?.gain ?? 1, lagChords: v })),

      setFlaperonHingeX: (v) => mutate((d) => (d.design.flaperonHinge.pivot[0] = v)),
      setFlaperonHingeZ: (v) => mutate((d) => (d.design.flaperonHinge.pivot[1] = v)),

      setFlaperonUpperCutX: (v) => mutate((d) => (d.design.flaperon.upperCutX = v)),
      setFlaperonLowerCutX: (v) => mutate((d) => (d.design.flaperon.lowerCutX = v)),
      setFlaperonNoseTipX: (v) => mutate((d) => (d.design.flaperon.noseTip[0] = v)),
      setFlaperonNoseTipZ: (v) => mutate((d) => (d.design.flaperon.noseTip[1] = v)),

      setVaneTeX: (v) => mutate((d) => (d.design.vane.te[0] = v)),
      setVaneTeZ: (v) => mutate((d) => (d.design.vane.te[1] = v)),
      setVanePoint: (i, coord, v) =>
        mutate((d) => {
          const p = d.design.vane.points[i];
          if (p) p[coord] = v;
        }),
      setVaneElementTe: (id, coord, v) =>
        mutate((d) => setVaneElement(d, id, (shape) => (shape.te[coord] = v))),
      setVaneElementPoint: (id, i, coord, v) =>
        mutate((d) =>
          setVaneElement(d, id, (shape) => {
            const p = shape.points[i];
            if (p) p[coord] = v;
          }),
        ),
      setFlaperonElementField: (id, field, v) => mutate((d) => setFlaperonElement(d, id, field, v)),
      setElementDeflection: (id, deg) => mutate((d) => setElementDeflection(d, id, deg)),
      setElementCoveDepth: (id, depth) => mutate((d) => setElementCoveDepth(d, id, depth)),
      setElementSlotDepth: (id, depth) => mutate((d) => setElementSlotDepth(d, id, depth)),
      setKruegerField: (id, field, v) => mutate((d) => setKruegerField(d, id, field, v)),
      setKruegerVariant: (id, variant) => mutate((d) => setKruegerVariant(d, id, variant)),
      setMainDroop: (field, v) => mutate((d) => setMainDroop(d, field, v)),
      setMainCoords: (coords) => mutate((d) => setMainCoords(d, coords)),
      setElementFrise: (id, on) => mutate((d) => setElementFrise(d, id, on)),

      setDiskX: (v) => mutate((d) => setDiskField(d, 'x', v)),
      setDiskZ: (v) => mutate((d) => setDiskField(d, 'z', v)),
      setDiskDiameter: (v) => mutate((d) => setDiskField(d, 'diameter', v)),
      setDiskTiltDeg: (v) => mutate((d) => setDiskField(d, 'tiltDeg', v)),
      setBlowingSourceField: (id, field, v) => mutate((d) => setBlowingSourceField(d, id, field, v)),

      setControlPoint: (handle, worldX, worldZ) =>
        mutate((d) => {
          switch (handle) {
            case 'vane-te':
              d.design.vane.te[0] = worldX;
              d.design.vane.te[1] = worldZ;
              return;
            case 'vane-0':
            case 'vane-1':
            case 'vane-2':
            case 'vane-3': {
              const i = Number(handle.slice('vane-'.length));
              const p = d.design.vane.points[i];
              if (p) {
                p[0] = worldX;
                p[1] = worldZ;
              }
              return;
            }
            case 'flap-nosetip':
              d.design.flaperon.noseTip[0] = worldX;
              d.design.flaperon.noseTip[1] = worldZ;
              return;
            case 'deploy-hinge':
              d.design.deployHinge.pivot[0] = worldX;
              d.design.deployHinge.pivot[1] = worldZ;
              return;
            case 'flaperon-hinge':
              d.design.flaperonHinge.pivot[0] = worldX;
              d.design.flaperonHinge.pivot[1] = worldZ;
              return;
            case 'prop-center':
              setDiskField(d, 'x', worldX);
              setDiskField(d, 'z', worldZ);
              return;
            default:
              return;
          }
        }),
    }),
    [mutate],
  );

  // ---- derived (memoised off the live config) ---------------------------------------------------
  const configuration = useMemo<Configuration>(() => buildConfiguration(config), [config]);

  const metrics = useMemo<GapMetrics>(
    () =>
      gapMetrics({
        main: configuration.main,
        vane: configuration.vane,
        flaperon: configuration.flaperon,
        // Pass the FULL deployed collection so `slots` enumerates every consecutive chordwise
        // slot (each carrying its element ids) — the Rig table reads per-element gap/overlap from
        // `slots` keyed by `downstreamId`. For the default triple this reproduces the two legacy slots.
        elements: configuration.elements.map((e) => ({ id: e.id, contour: e.contour })),
      }),
    [configuration],
  );

  const metricOutputs = useMemo<Record<string, number>>(() => gapMetricOutputs(metrics), [metrics]);

  // Gap check across the RANGE OF MOTION (FXC-9009 #8): sweep the deploy + flaperon limits and
  // report per-slot min gap + interference. The sweep rebuilds geometry ~27x (tens of ms), so derive
  // it from a DEBOUNCED config — a knob drag stays smooth and the check recomputes ~250ms after edits
  // settle rather than on every intermediate frame.
  const debouncedConfig = useDebounced(config, 250);
  const motionGap = useMemo<MotionGapCheck>(() => motionGapCheck(debouncedConfig), [debouncedConfig]);

  const elements = useMemo<Element[]>(() => elementsOf(config), [config]);
  const blowing = useMemo<BlowingSource[]>(() => blowingOf(config), [config]);

  // Pass the already-built `configuration` (see above) so this doesn't rebuild the full multi-element
  // contour a SECOND time on every config change — `buildSweepInputs` previously called
  // `buildConfiguration(config)` internally, independently of the `configuration` useMemo above, so
  // every drag/animation frame paid for the contour build twice (FXC-9009 — this compounded with the
  // motionGap/canvas fixes to make a sustained deploy-angle animation visibly jerky).
  const sweepInputs = useMemo<SweepInputs>(() => buildSweepInputs(config, configuration), [config, configuration]);

  // AC-H — data-driven handle descriptors, regenerated whenever the config (or its collection) changes.
  const handleDescriptors = useMemo<FoilHandleDescriptor[]>(() => foilHandleDescriptors(config), [config]);

  // Buffer↔baseline dirty model: the live config's design fingerprint, and whether it has diverged
  // from the baseline (a reshape happened → needs commit/promote to become a tracked variant).
  const bufferFingerprint = useMemo<string>(() => designFingerprint(config), [config]);
  const isDirty = useMemo<boolean>(() => bufferFingerprint !== baseline.fingerprint, [bufferFingerprint, baseline.fingerprint]);

  // Apply a dragged handle by id onto a fresh clone via the descriptor's own `set` closure.
  const setHandleDescriptor = useCallback(
    (handleId: string, worldX: number, worldZ: number): void => {
      mutate((draft) => {
        // Re-derive the descriptor on the DRAFT so its `set` writes onto the cloned objects (the
        // descriptors captured by `handleDescriptors` close over the previous render's config refs).
        const desc = foilHandleDescriptors(draft).find((h) => h.id === handleId);
        desc?.set(draft, worldX, worldZ);
      });
    },
    [mutate],
  );

  const flow = useMemo<FlowConditions>(
    () =>
      flowConditions(
        config.main.chord,
        config.operation.freestreamKnots,
        config.operation.densityAltitudeM,
        config.operation.tempOffsetK,
      ),
    [config],
  );

  // ---- NN-Newton design-opt state (DESIGN §8) ---------------------------------------------------
  // Design-range sidecar (NOT in the config ⇒ never enters the fingerprint, like `operation`).
  const [ranges, setRanges] = useState<RangeMap>({});
  // The bound flowfield-NN (weights fetched at runtime; null until a model is loaded in). A ref backs
  // the async loops so a freshly-set NN is visible to an in-flight callback without a stale closure.
  const [nnModel, setNnModel] = useState<FoilNN | null>(null);
  const nnRef = useRef<FoilNN | null>(null);

  const setRange = useCallback((key: string, range: ParamRange): void => {
    setRanges((prev) => ({ ...prev, [key]: range }));
  }, []);
  const clearRange = useCallback((key: string): void => {
    setRanges((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, key)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const setNN = useCallback((model: FoilNN | null): void => {
    nnRef.current = model;
    setNnModel(model);
  }, []);

  const buildSpecs = useCallback((): ParamSpec[] => buildSpecsFor(config, ranges), [config, ranges]);

  // NN condition for the live design (α/blow from the model, Mach/Re from the flow state).
  const nnCond = useMemo<NNCond | null>(
    () => (nnModel ? nnCondFor(config, flow, nnModel) : null),
    [config, flow, nnModel],
  );

  // Predicted surface Cp from the bound NN (honest-null when no model is bound).
  const surfaceCp = useMemo<SurfaceCpElement[] | null>(() => {
    if (!nnModel || !nnCond) return null;
    const els = nnElementsOf(configuration);
    return surfaceCpFromNN(nnModel.nn, nnCond, els, { forward: nnModel.forward });
  }, [nnModel, nnCond, configuration]);

  const runInverseDesign = useCallback(
    async (target: SurfaceCp, opts?: RunInverseDesignOpts): Promise<InverseDesignResult> => {
      const model = nnRef.current;
      const empty: InverseDesignResult = { applied: false, finalCost: NaN, history: [] };
      if (!model) return empty;
      const specs = buildSpecsFor(config, ranges);
      if (specs.length === 0) return empty;
      const cond = nnCondFor(config, flow, model);
      const cpAt = cpAtFactory<HighLiftAirfoil>(
        model.nn,
        cond,
        config,
        specs,
        (design) => nnElementsOf(buildConfiguration(design)),
        {
          applyParams: applyParamsToDesign,
          element: opts?.element,
          surfaceOpts: { forward: model.forward },
        },
      );
      const res = solveInverse({ specs, cpAt, target, region: opts?.region, opts: opts?.lm });
      // Apply the recovered UNLOCKED DOFs back onto the live config (locked DOFs are untouched).
      const unlocked = res.specs.filter((s) => !s.locked);
      if (unlocked.length > 0) mutate((d) => applyAxesToConfig(d, specsToAxes(unlocked)));
      return { applied: true, finalCost: res.finalCost, history: res.history };
    },
    [config, ranges, flow, mutate],
  );

  const runOptimize = useCallback(
    async (objective: FoilObjectiveKind, opts?: RunOptimizeOpts): Promise<OptimizeResult> => {
      const model = nnRef.current;
      const empty: OptimizeResult = { applied: false, finalValue: NaN, history: [] };
      if (!model) return empty;
      const specs = buildSpecsFor(config, ranges);
      if (specs.length === 0) return empty;
      const cond = nnCondFor(config, flow, model);

      // Scalar objective over the FULL design vector (spec order) — mirrors cpAtFactory's writeback
      // convention so optimizeScalar/solveInverse drive identical machinery. All forms MINIMISE.
      const scalar = (vec: number[]): number => {
        const stepped = unpackFullVector(specs, vec);
        const design = applyParamsToDesign(config, stepped);
        const els = nnElementsOf(buildConfiguration(design));
        if (objective === 'capSuction') {
          const strips = surfaceCpFromNN(model.nn, cond, els, { forward: model.forward });
          const want = opts?.element ? strips.find((s) => s.element === opts.element) : strips[0];
          const cp = want ? peakSuction(want).cp : 0;
          return -cp; // push the most-negative Cp toward 0 (cap suction)
        }
        const f = nnSurfaceForces(model.nn, cond, els);
        if (objective === 'maxCL') return -f.CL;
        if (objective === 'minCD') return f.CD;
        // maxLD: maximise CL/CD (guard a vanishing CD).
        const cd = Math.abs(f.CD) > 1e-9 ? f.CD : 1e-9 * Math.sign(f.CD || 1);
        return -(f.CL / cd);
      };

      const res = optimizeScalar({ specs, objective: scalar, opts: opts?.nm });
      const unlocked = res.specs.filter((s) => !s.locked);
      if (unlocked.length > 0) mutate((d) => applyAxesToConfig(d, specsToAxes(unlocked)));
      return { applied: true, finalValue: res.finalValue, history: res.history };
    },
    [config, ranges, flow, mutate],
  );

  // Inverse-design a GAP-FLOW target (FXC-9009 #10): drive a slot metric (e.g. slot_vf_gap,
  // slot_mv_convergence) to a target value over the unlocked ranged DOFs. PURE GEOMETRY — it reads
  // gapMetricOutputs(buildConfiguration), so unlike the Cp / force objectives it needs NO bound NN.
  const runGapTarget = useCallback(
    async (
      metricKey: string,
      targetValue: number,
      opts?: { nm?: NelderMeadOptions },
    ): Promise<GapTargetResult> => {
      const r = solveGapTarget(config, ranges, metricKey, targetValue, opts);
      if (r.applied && Number.isFinite(r.finalValue)) {
        // Write back ONLY the recovered unlocked DOFs onto the live config (locked DOFs untouched).
        const unlocked = r.specs.filter((s) => !s.locked);
        if (unlocked.length > 0) mutate((d) => applyAxesToConfig(d, specsToAxes(unlocked)));
      }
      return { applied: r.applied, finalError: r.finalError, finalValue: r.finalValue, history: r.history };
    },
    [config, ranges, mutate],
  );

  // PURE geometric flap-deployment optimizer (FXC-9009): first-principles clearance + slot-shape
  // health over the deployment's own knobs, no NN/backend. See `solveGeometricOptimize`.
  const runGeometricOptimize = useCallback(
    async (opts?: { nm?: NelderMeadOptions }): Promise<GeometricOptimizeResult> => {
      const r = solveGeometricOptimize(config, opts);
      if (r.applied) {
        // Write back ONLY the recovered unlocked DOFs onto the live config (locked DOFs untouched).
        const unlocked = r.specs.filter((s) => !s.locked);
        if (unlocked.length > 0) mutate((d) => applyAxesToConfig(d, specsToAxes(unlocked)));
      }
      return r;
    },
    [config, mutate],
  );

  return {
    config,
    setters,
    applyAxes,
    setKnobConstraint,
    handleDescriptors,
    setHandleDescriptor,
    setConfig,
    loadConfig,
    commitBaseline,
    baseline,
    isDirty,
    bufferFingerprint,
    reset,
    elements,
    blowing,
    addVane,
    addPlainFlap,
    addSlat,
    addKrueger,
    addSlottedFlap,
    addSingleSlottedFlap,
    addBlowing,
    removeCollectionItem,
    configuration,
    gapMetrics: metrics,
    gapMetricOutputs: metricOutputs,
    motionGap,
    sweepInputs,
    flowConditions: flow,
    ranges,
    setRange,
    clearRange,
    buildSpecs,
    setNN,
    hasNN: nnModel !== null,
    surfaceCp,
    runInverseDesign,
    runOptimize,
    runGapTarget,
    runGeometricOptimize,
  };
}
